/**
 * String interning storage for memory optimization.
 *
 * String interning stores only one copy of each unique string value.
 * All references to the same string share the single stored copy,
 * significantly reducing memory usage when many objects contain
 * duplicate string values (e.g., cadFamily, cadMaterial fields
 * repeated across thousands of nodes).
 *
 * Usage:
 * - add(string) returns an index (finds existing or adds new)
 * - get(index) returns the original string
 * - Index 0 represents null/undefined
 *
 * @example
 * ```typescript
 * const storage = new StringStorage();
 * const idx1 = storage.add("Basic Wall");  // returns 1
 * const idx2 = storage.add("Basic Wall");  // returns 1 (same index, no duplicate)
 * const idx3 = storage.add("Floor");       // returns 2
 * storage.get(1);  // returns "Basic Wall"
 * storage.get(0);  // returns null
 * ```
 */
export class StringStorage {
  /** Array of interned strings. Index 0 is reserved for null/undefined. */
  private strings: string[] = [''];

  /** Map from string value to its index for O(1) lookup. */
  private index: Map<string, number> = new Map();

  /**
   * Adds a string to the storage and returns its index.
   * If the string already exists, returns the existing index.
   * If the string is null or undefined, returns 0.
   *
   * @param s - The string to intern
   * @returns The index of the string in storage (0 for null/undefined)
   */
  add(s: string | null | undefined): number {
    if (s === null || s === undefined) {
      return 0;
    }

    const existing = this.index.get(s);
    if (existing !== undefined) {
      return existing;
    }

    const idx = this.strings.length;
    this.strings.push(s);
    this.index.set(s, idx);
    return idx;
  }

  /**
   * Retrieves a string by its index.
   *
   * @param i - The index of the string
   * @returns The string at the given index, or null if index is 0
   */
  get(i: number): string | null {
    if (i === 0) {
      return null;
    }
    return this.strings[i] ?? null;
  }

  /**
   * Returns the number of unique strings stored (excluding the null placeholder).
   */
  get size(): number {
    return this.strings.length - 1;
  }

  /**
   * Clears all stored strings and resets the storage.
   * Call this when the interned data is no longer needed to free memory.
   */
  clear(): void {
    this.strings = [''];
    this.index.clear();
  }
}
